import { memo, type ReactNode, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { type BaseCustomField } from '@air/api/types';
import { useShortUrlListCustomFields } from '@air/feature-custom-fields/hooks';
import { Spinner } from '@air/primitive-spinner';
import { Switch } from '@air/primitive-switch';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';

import {
  type ContentCollectionState,
  contentCollectionApplyAllSelector,
  contentCollectionFormSelector,
  contentCollectionTouchedSelector,
  setApplyAllAction,
  setTouchedAction,
} from '../redux';
import { ContentCollectionApplyAllModal } from './ContentCollectionApplyAllModal';
import { ContentCollectionCustomFieldsList } from './ContentCollectionCustomFieldList';

export type ContentCollectionSidebarCustomFieldsProps = {
  renderCustomField: (props: { customField: BaseCustomField; disabled: boolean }) => ReactNode;
};

export const ContentCollectionSidebarCustomFields = memo(
  ({ renderCustomField }: ContentCollectionSidebarCustomFieldsProps) => {
    const dispatch = useDispatch();
    const store = useStore<{ contentCollection: ContentCollectionState }>();
    const applyAll = useSelector(contentCollectionApplyAllSelector);
    const form = useSelector(contentCollectionFormSelector);
    const { shortId } = useShortIdContext();
    const { data, isLoading } = useShortUrlListCustomFields({ shortId: shortId });
    const [showApplyAllModal, hideApplyAllModal] = useAirModal(ContentCollectionApplyAllModal);

    const customFields = useMemo(() => {
      if (!data) {
        return [];
      }

      const customFields = form.fields.reduce<(BaseCustomField & { required: boolean })[]>((customFields, field) => {
        const customField = data.data.find((customField) => customField.id === field.customFieldId);

        if (customField) {
          customFields.push({
            ...customField,
            required: field.required,
          });
        }

        return customFields;
      }, []);

      return customFields;
    }, [data, form.fields]);

    const onCheckedChange = useCallback(
      (checked: boolean) => {
        const touched = contentCollectionTouchedSelector(store.getState());

        if (checked && touched) {
          showApplyAllModal({
            onApplyAll: () => {
              dispatch(setApplyAllAction({ applyAll: checked }));
              dispatch(setTouchedAction({ touched: false }));
              hideApplyAllModal();
            },
          });
        } else {
          dispatch(setApplyAllAction({ applyAll: checked }));
        }
      },
      [dispatch, hideApplyAllModal, showApplyAllModal, store],
    );

    if (isLoading) {
      return <Spinner className="mx-auto" />;
    }
    if (customFields.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-4" data-testid="CONTENT_COLLECTION_FIELDS_LIST">
        <p className="font-medium ads:text-secondary text-14 text-gray-11">Fill out the fields below:</p>

        <div className="flex items-center justify-between ads:bg-raised bg-gray-2 p-3">
          <label
            className="font-medium ads:text-primary text-14 text-gray-12"
            htmlFor="content-collection-apply-all-switch"
          >
            Apply these to all files
          </label>
          <div className="flex items-center gap-2">
            <span className="ads:text-metadata text-12 text-gray-9">{applyAll ? 'ON' : 'OFF'}</span>
            <Switch
              checked={applyAll}
              data-testid="CONTENT_COLLECTION_APPLY_ALL_SWITCH"
              id="content-collection-apply-all-switch"
              onCheckedChange={onCheckedChange}
            />
          </div>
        </div>

        <ContentCollectionCustomFieldsList
          className="gap-6 px-0.5"
          customFields={customFields}
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the color pairing
          renderCustomField={({ customField }) => renderCustomField({ customField, disabled: !applyAll })}
        />
      </div>
    );
  },
);

ContentCollectionSidebarCustomFields.displayName = 'ContentCollectionSidebarCustomFields';
